// What the home page's cards show of each dataset: its name, group, item count and the image of
// its best-known item. Read from the dataset files once, into /data/summary.json, so that the home
// page loads none of them: the build writes it (scripts/build-seo.ts) and the dev server makes it
// on request (vite.config.ts).
import type { TDataset, TDatasetGroup, TManifest } from '@/types/index.js';
import { getFirstImage } from '@/utils/data.js';
import { getImageCopies } from '@/utils/image-levels.js';

export const SUMMARY_URL = '/data/summary.json';

// The smallest copy at least this many pixels on its long side: a card's image is about 200 CSS
// pixels tall, drawn at up to twice that on a high-density screen
const CARD_IMAGE_SIDE = 512;

export type TDatasetSummary = {
	id: string;
	title: string;
	// The title of the group it is listed under in the manifest
	group: string;
	// "411 characters"
	count: string;
	image: string | null;
	// Its name, for the image's alternative text
	imageItem: string | null;
	// The image in the middle of the card, not on its ground line (see TDatasetMetadata)
	centred?: true;
};

const countFormat = new Intl.NumberFormat('en-US');

/**
 * One dataset's card. Its image is the `heroItem` of its metadata, or else its middle item, which
 * is of a typical size.
 */
export const summariseDataset = (
	entry: { id: string; title: string; imageLevels?: number[] },
	group: string,
	{ metadata, items }: TDataset
): TDatasetSummary => {
	const hero = items.find((item) => item.name === metadata.heroItem) ?? items[Math.floor(items.length / 2)];
	const url = hero && getFirstImage(hero);
	const copies = url ? getImageCopies(url, entry.imageLevels) : [];
	const copy = copies.find((candidate) => candidate.maxSide >= CARD_IMAGE_SIDE) ?? copies.at(-1);

	return {
		id: entry.id,
		title: entry.title,
		group,
		count: `${countFormat.format(items.length)} ${metadata.itemsLabel ?? 'items'}`,
		image: copy?.url ?? null,
		imageItem: hero?.name ?? null,
		...(metadata.heroCentred ? { centred: true as const } : {}),
	};
};

const summariseGroup = (group: TDatasetGroup, readDataset: (file: string) => TDataset): TDatasetSummary[] => [
	...(group.datasets ?? []).map((entry) => summariseDataset(entry, group.title, readDataset(entry.file))),
	...(group.children ?? []).flatMap((child) => summariseGroup(child, readDataset)),
];

/** Every dataset of the manifest, in menu order. `readDataset` reads a dataset file by its URL. */
export const summariseManifest = (
	manifest: TManifest,
	readDataset: (file: string) => TDataset
): TDatasetSummary[] => manifest.groups.flatMap((group) => summariseGroup(group, readDataset));
